import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import styled from 'styled-components';

import useJobsHook from '../../../hooks/useJobs.hook';
import useStatusHook from '../../../hooks/useStatus.hook';
import useSourceHook from '../../../hooks/useSource.hook';
import useRegionHook from '../../../hooks/useRegion.hook';
import useEmployeeHook from '../../../hooks/useEmployee.hook';
import useExperienceHook from '../../../hooks/useExperience.hook';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';

import { getFilters } from '../../../services/router.service';
import { getGlobalRegionSelected } from '../../../redux/Region/Region.slice';

import DateRangeModalComponent from '../../../components/Modal/DateRangeModal.component';
import InputComponent from '../../../components/Input/Input.component';
import SelectComponent, {
  SelectGroupedOption,
} from '../../../components/Input/Select.component';
import { StyledCard } from '../../../components/Card.component';
import ButtonComponent from '../../../components/Button.component';

import {
  reportHeader,
  chartVarieties,
} from '../../../constants/report.constant';
import { NOT_ASSIGN_ITEM } from '../../../constants/filters.constant';
import { DATE_FORMAT } from '../../../constants/date.contant';
import useDepartmentHook from '../../../hooks/useDepartment.hook';
import HRSelect from '../../../components/Input/HRSelect.component';
import RecruiterSelect from '../../../components/Input/RecruiterSelect.component';
import useEnglishLevel from '../../../hooks/useEnglishLevel.hook';
import { getLocalStorage, setLocalStorage } from '../../../utils/storage';

export interface ReportParamsFormProps {
  onApply: Function;
  hide?: { [key: string]: boolean };
  group: string | undefined;
}

const StyledStatusGroupLabel = styled.span``;
const StyledStatusGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const StyledParamsBlockParams = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 90vh;
  overflow: auto;
  margin: 0 -10px;
  padding: 0 10px;
`;
export const StyledParamsBlock = styled(StyledCard)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 18px 20px;
`;

export const RECORDS_OPTIONS = [
  { value: 'all', label: 'All records' },
  { value: 'last', label: 'Last record' },
];

export const CHART_TYPE_OPTIONS = [
  { value: chartVarieties.DONUT, label: 'chart_type.donut' },
  {
    value: chartVarieties.BAR,
    label: 'chart_type.bar',
  },
  { value: chartVarieties.HORIZONTAL, label: 'chart_type.horizontal' },
];

const ReportParamsForm = ({
  onApply,
  hide = {},
  group,
}: ReportParamsFormProps) => {
  const { t } = useTranslation();
  const globalRegion = useAppSelector(getGlobalRegionSelected);
  const { openModal } = useModalManager();
  const currentDate = dayjs().unix() * 1000;
  const [form, setForm] = useState<any>({
    jobs: [],
    selectAll: undefined,
    hr: undefined,
    recruiter: undefined,
    status: undefined,
    source: undefined,
    region: undefined,
    department: undefined,
    experience: undefined,
    english: undefined,
    dateRange: undefined,
    subGroupBy: undefined,
  });
  const { getSelectList: getJobsList, getSelectListByIds: getJobsById } =
    useJobsHook();
  const {
    getSelectList: getPersonExperienceList,
    getSelectListByIds: getPersonExperienceById,
  } = useExperienceHook();
  const {
    getSelectList: getEmployeeList,
    getSelectListByIds: getEmployeeById,
  } = useEmployeeHook();

  const { getSelectList: getEnglishList, getSelectListByIds: getEnglishById } =
    useEnglishLevel();

  const {
    getSelectWholeList: getStatusList,
    getSelectListByIds: getStatusById,
  } = useStatusHook();
  const { getSelectList: getSourceList, getSelectListByIds: getSourceById } =
    useSourceHook();
  const { getSelectList: getRegionList, getSelectListByIds: getRegionById } =
    useRegionHook();
  const {
    getSelectList: getDepartmentList,
    getSelectListByIds: getDepartmentById,
  } = useDepartmentHook();
  const [switches, setSwitches] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setForm((prev: any) => ({
      ...prev,
      region: globalRegion || undefined,
    }));
  }, [globalRegion]);

  useEffect(() => {
    const savedParams = group ? getLocalStorage(group) : '';
    const parsedSavedParams = savedParams ? JSON.parse(savedParams) : {};
    const jobs = getFilters(window.location.search, 'jobs') || parsedSavedParams.jobs;
    const hr = getFilters(window.location.search, 'hr') || parsedSavedParams.hr;
    const recruiter = getFilters(window.location.search, 'recruiter') || parsedSavedParams.recruiter;
    const status = getFilters(window.location.search, 'status') || parsedSavedParams.status;
    const source = getFilters(window.location.search, 'source') || parsedSavedParams.source;
    const region = getFilters(window.location.search, 'region') || parsedSavedParams.region;
    const department = getFilters(window.location.search, 'department') || parsedSavedParams.department;
    const experience = getFilters(window.location.search, 'experience') || parsedSavedParams.experience;
    const english = getFilters(window.location.search, 'english') || parsedSavedParams.english;
    const dateRange: any = getFilters(window.location.search, 'dateRange') || parsedSavedParams.dateRange;
    const selectAll = getFilters(window.location.search, 'selectAll') || parsedSavedParams.selectAll;
    const subGroupBy = getFilters(window.location.search, 'subGroupBy') || parsedSavedParams.subGroupBy;

    if (subGroupBy) {
      const currentSubGroupDetails =
        reportHeader.find(sg => sg.value === subGroupBy) ||
        ({} as {
          value: string;
          text: string;
        });
      setForm((prev: any) => ({
        ...prev,
        subGroupBy: {
          value: currentSubGroupDetails.value,
          label: t(currentSubGroupDetails.text),
        },
      }));
    }
    if (jobs) {
      const { value, switchValue } = jobs as { value: unknown[]; switchValue?: boolean };

      if (Array.isArray(value)) {
        const jobIds = value.filter((id): id is number => typeof id === 'number');

        getJobsById(jobIds).then((loadedJobs: any) => {
          setForm((prev: any) => ({
            ...prev,
            jobs: loadedJobs,
          }));

          setSwitches((prev) => ({ ...prev, jobs: switchValue ?? false }));
        });
      }
    }
    if (hr) {
      getEmployeeById([hr as string], 'hr').then((hrs: any) => {
        const presetHR = getPresetFilterValue(
          hr as (number | string)[],
          !!subGroupBy,
          hrs,
        );
        setForm((prev: any) => ({
          ...prev,
          hr: presetHR,
        }));
      });
    }
    if (recruiter) {
      getEmployeeById([recruiter as string], 'recruiter').then(
        (recruiters: any) => {
          const presetRecruiter = getPresetFilterValue(
            recruiter as (number | string)[],
            !!subGroupBy,
            recruiters,
          );
          setForm((prev: any) => ({
            ...prev,
            recruiter: presetRecruiter,
          }));
        },
      );
    }
    if (status) {
      getStatusById([status as number]).then((statuses: any) => {
        const presetStatus = getPresetFilterValue(
          status as (number | string)[],
          !!subGroupBy,
          statuses,
        );
        setForm((prev: any) => ({
          ...prev,
          status: presetStatus,
        }));
      });
    }
    if (source) {
      getSourceById([source as number]).then((sources: any) => {
        const presetSource = getPresetFilterValue(
          source as (number | string)[],
          !!subGroupBy,
          sources,
        );
        setForm((prev: any) => ({
          ...prev,
          source: presetSource,
        }));
      });
    }
    if (region) {
      getRegionById([region as number]).then((regions: any) => {
        const presetRegion = getPresetFilterValue(
          region as (number | string)[],
          !!subGroupBy,
          regions,
        );
        setForm((prev: any) => ({
          ...prev,
          region: presetRegion,
        }));
      });
    }
    if (department) {
      getDepartmentById([department as number]).then((departments: any) => {
        const presetDepartment = getPresetFilterValue(
          department as (number | string)[],
          !!subGroupBy,
          departments,
        );
        setForm((prev: any) => ({
          ...prev,
          department: presetDepartment,
        }));
      });
    }
    if (experience) {
      getPersonExperienceById([experience as number]).then(
        (experiences: any) => {
          const presetExperience = getPresetFilterValue(
            experience as (number | string)[],
            !!subGroupBy,
            experiences,
          );
          setForm((prev: any) => ({
            ...prev,
            experience: presetExperience,
          }));
        },
      );
    }
    if (english) {
      getEnglishById([english as number]).then((englishs: any) => {
        const presetenglish = getPresetFilterValue(
          englishs as (number | string)[],
          !!subGroupBy,
          englishs,
        );
        setForm((prev: any) => ({
          ...prev,
          english: presetenglish,
        }));
      });
    }

    if (dateRange && dateRange.start && dateRange.end) {
      setForm((prev: any) => ({
        ...prev,
        dateRange,
      }));
    }

    if (selectAll) {
      setForm((prev: any) => ({
        ...prev,
        selectAll: true,
      }));
    }
  }, []);

  const getPresetFilterValue = (
    selectedValues: (number | string)[],
    subGroupBy: boolean,
    valuesById: {
      value: string | number;
      label: any;
    }[],
  ) => {
    let presetValue;

    if (subGroupBy) {
      if (
        (selectedValues as (number | string)[]).includes(
          NOT_ASSIGN_ITEM.value.toString(),
        )
      ) {
        presetValue = [...valuesById, NOT_ASSIGN_ITEM];
      } else {
        presetValue = [...valuesById];
      }
    } else {
      presetValue = (valuesById.length && valuesById) || NOT_ASSIGN_ITEM;
    }

    return presetValue;
  };

  const handleApplyParams = useCallback(() => {
    const params: any = {};
    const prepareParamValue = (form: any, formKey: any) => {
      if (!form[formKey] && typeof form[formKey] !== 'boolean') {
        return undefined;
      }

      if (Array.isArray(form[formKey])) {
        const value = form[formKey].map((item: any) => {
          return item.value ? item.value : item;
        });
        return value.length ? value : undefined;
      }

      return form[formKey].value ? form[formKey].value : form[formKey];
    };

    for (const formKey in form) {
      if (formKey === 'jobs') {
        const value = prepareParamValue(form, formKey);
        if (value?.length) {
          params[formKey] = {
            value,
            switchValue: switches['jobs'] || false,
          };
        }
      } else {
        params[formKey] = prepareParamValue(form, formKey);
      }
    }

    onApply(params);
    if (group) setLocalStorage(group, JSON.stringify(params));
  }, [form, onApply, switches]);

  const handleChangeParams = (name: string) => (value: any) => {
    if (name === 'subGroupBy') {
      //if value of the second grouping resets
      if (!value) {
        setForm((prev: any) =>
          Object.keys(prev).reduce(
            (acc, fieldName) => {
              //values of all fields that were multiselects for the second grouping don't have to be arrays anymore
              if (Array.isArray(prev[fieldName]) && fieldName !== 'jobs')
                acc = {
                  ...acc,
                  [fieldName]: prev[fieldName][0],
                };
              return acc;
            },
            { [name]: value },
          ),
        );
      } else {
        setForm((prev: any) =>
          !prev[name] // if there was no value for the second grouping and now it is set, we clean all other fields of the form
            ? Object.keys(prev).reduce(
                (acc, fieldName) => {
                  if (fieldName !== name) acc = { ...acc, [fieldName]: null };
                  return acc;
                },
                { [name]: value },
              )
            : {
                // if we want to change the second grouping, just set new value for this field
                ...prev,
                [name]: value,
              },
        );
      }
    } else {
      setForm((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSwitchChange = useCallback((key: string, checked: boolean) => {
    handleChangeParams(key)(checked ? [] : null);

    setSwitches((prev) => ({
      ...prev,
      [key]: checked,
    }));
  }, []);

  const formatStatusGroupLabel = (data: SelectGroupedOption) => (
    <StyledStatusGroup>
      <StyledStatusGroupLabel>{data.label}</StyledStatusGroupLabel>
    </StyledStatusGroup>
  );

  const formatDateRange = useMemo(() => {
    if (!form.dateRange) {
      return undefined;
    }

    return `${dayjs(form.dateRange.start).format(DATE_FORMAT)} - ${dayjs(
      form.dateRange.end,
    ).format(DATE_FORMAT)}`;
  }, [form]);

  const selectRecordsOptions = useMemo(
    () => RECORDS_OPTIONS.map(obj => ({ ...obj, label: t(obj.label) })),
    [],
  );
  const selectSubGroupByOptions = useMemo(
    () =>
      reportHeader.reduce((acc, obj) => {
        if (obj.value === group) return acc;

        if (
          (group === 'hr' && obj.value === 'recruiter') ||
          (group === 'recruiter' && obj.value === 'hr')
        ) {
          return acc;
        }

        return [...acc, { value: obj.value, label: t(obj.text) }];
      }, [] as { value: string; label: string }[]),
    [],
  );

  return (
    <StyledParamsBlock>
      <StyledParamsBlockParams>
        <SelectComponent
          label={t('Group by')}
          value={form.subGroupBy}
          options={selectSubGroupByOptions}
          placeholder={t('Choose second group')}
          onChangeOption={handleChangeParams('subGroupBy')}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('Records')}
          value={
            form.selectAll ? selectRecordsOptions[0] : selectRecordsOptions[1]
          }
          options={selectRecordsOptions}
          onChangeOption={({ value }: any) =>
            handleChangeParams('selectAll')(
              value === selectRecordsOptions[0].value || undefined,
            )
          }
          useMenuPortalTarget
        />
        {!hide.jobs && (
          <SelectComponent
            label={t('Jobs')}
            value={form.jobs}
            placeholder={t('Choose job(-s)')}
            initOptions={[NOT_ASSIGN_ITEM]}
            isMulti
            loadOptions={getJobsList}
            onChangeOption={handleChangeParams('jobs')}
            isSwitchable={true}
            switchValue={switches['jobs'] || false}
            onSwitchChange={(checked) => handleSwitchChange('jobs', checked)}
            isOptionDisabled = {switches['jobs'] && form.jobs?.length >= 3}
            leftSwitchLabel = {t('or')}
            rightSwitchLabel  = {t('and')}
            useMenuPortalTarget
          />
        )}

        <HRSelect
          value={form.hr}
          onChange={handleChangeParams('hr')}
          isMulti
          notAssignItem={true}
          useMenuPortalTarget
        />

        <RecruiterSelect
          value={form.recruiter}
          onChange={handleChangeParams('recruiter')}
          isMulti
          notAssignItem={true}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('Status')}
          value={form.status}
          initOptions={[NOT_ASSIGN_ITEM]}
          placeholder={t('Choose status')}
          formatGroupLabel={formatStatusGroupLabel}
          isMulti
          loadOptions={getStatusList}
          onChangeOption={handleChangeParams('status')}
          selectGroup={true}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('Source')}
          placeholder={t('Choose source')}
          value={form.source}
          initOptions={[NOT_ASSIGN_ITEM]}
          isMulti
          loadOptions={getSourceList}
          onChangeOption={handleChangeParams('source')}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('Region')}
          placeholder={t('Choose region')}
          value={form.region}
          initOptions={[NOT_ASSIGN_ITEM]}
          isSearchable={false}
          isMulti
          loadOptions={getRegionList}
          onChangeOption={handleChangeParams('region')}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('Department')}
          placeholder={t('Choose department')}
          isMulti
          isSearchable={false}
          value={form.department}
          initOptions={[NOT_ASSIGN_ITEM]}
          loadOptions={getDepartmentList}
          onChangeOption={handleChangeParams('department')}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('Experience')}
          value={form.experience}
          initOptions={[NOT_ASSIGN_ITEM]}
          placeholder={t('Choose experience')}
          isSearchable={false}
          isMulti
          loadOptions={getPersonExperienceList}
          onChangeOption={handleChangeParams('experience')}
          useMenuPortalTarget
        />

        <SelectComponent
          label={t('English level')}
          value={form.english}
          initOptions={[NOT_ASSIGN_ITEM]}
          placeholder={t('Choose english level')}
          isSearchable={false}
          isMulti
          loadOptions={getEnglishList}
          onChangeOption={handleChangeParams('english')}
          useMenuPortalTarget
        />

        <InputComponent
          label={t('Date')}
          onClearClick={() => handleChangeParams('dateRange')(undefined)}
          onClick={() =>
            openModal(DateRangeModalComponent, {
              dateRange: form.dateRange,
              handleChange: handleChangeParams('dateRange'),
              withoutDaysAfter: true,
            })
          }
          value={formatDateRange}
          placeholder={t('Choose date')}
        />
      </StyledParamsBlockParams>
      <ButtonComponent appearance="primary" handleClick={handleApplyParams}>
        {t('Show results')}
      </ButtonComponent>
    </StyledParamsBlock>
  );
};

export default ReportParamsForm;
